import { addCardComment } from 'modules';
import { StandardThunkAction } from 'types';
import { Action, actionCreator } from 'util/actions';
import { createReducer } from 'util/reducers';

// Action types
const OPEN_REPLY = Symbol('ui/OPEN_REPLY');
const CLOSE_REPLY = Symbol('ui/CLOSE_REPLY');
const CLEAR_REPLIES = Symbol('ui/CLEAR_REPLIES');

const SUBMIT_REPLY_REQUEST = Symbol('ui/SUBMIT_REPLY_REQUEST');
const SUBMIT_REPLY_SUCCESS = Symbol('ui/SUBMIT_REPLY_SUCCESS');
const SUBMIT_REPLY_ERROR = Symbol('ui/SUBMIT_REPLY_ERROR');

const SET_NUM_BOARDS_VISIBLE = Symbol('ui/SET_NUM_BOARDS_VISIBLE');

type OpenReplyAction = Action<typeof OPEN_REPLY, { id: string }>;
type CloseReplyAction = Action<typeof CLOSE_REPLY, { id: string }>;
type ClearRepliesAction = Action<typeof CLEAR_REPLIES, null>;

type SubmitReplyRequestAction = Action<typeof SUBMIT_REPLY_REQUEST, null>;
type SubmitReplySuccessAction = Action<typeof SUBMIT_REPLY_SUCCESS, null>;
type SubmitReplyErrorAction = Action<typeof SUBMIT_REPLY_ERROR, Error>;

type SetNumBoardsVisibleAction = Action<
  typeof SET_NUM_BOARDS_VISIBLE,
  NumBoardsVisible
>;

interface NumBoardsVisible {
  recentlyViewed: number;
  starred: number;
}

export interface SuperHomeState {
  openReplies: string[];
  numBoardsVisible: NumBoardsVisible;
}

const initialState: SuperHomeState = {
  openReplies: [],
  numBoardsVisible: {
    recentlyViewed: 4,
    starred: 8,
  },
};

export default createReducer(initialState, {
  [OPEN_REPLY](state, { payload }: OpenReplyAction) {
    return {
      ...state,
      openReplies: [...state.openReplies, payload.id],
    };
  },

  [CLOSE_REPLY](state, { payload }: CloseReplyAction) {
    return {
      ...state,
      openReplies: state.openReplies.filter((openId) => openId !== payload.id),
    };
  },

  [CLEAR_REPLIES](state) {
    return { ...state, openReplies: [] };
  },

  [SET_NUM_BOARDS_VISIBLE](state, { payload }: SetNumBoardsVisibleAction) {
    return {
      ...state,
      numBoardsVisible: payload,
    };
  },
});

// Action creators
export const openReply = actionCreator<OpenReplyAction>(OPEN_REPLY);
export const closeReply = actionCreator<CloseReplyAction>(CLOSE_REPLY);
export const clearReplies = actionCreator<ClearRepliesAction>(CLEAR_REPLIES);

export const submitReplySuccess = actionCreator<SubmitReplySuccessAction>(
  SUBMIT_REPLY_SUCCESS,
);
export const submitReplyRequest = actionCreator<SubmitReplyRequestAction>(
  SUBMIT_REPLY_REQUEST,
);
export const submitReplyError = actionCreator<SubmitReplyErrorAction>(
  SUBMIT_REPLY_ERROR,
);

export const setNumBoardsVisible = actionCreator<SetNumBoardsVisibleAction>(
  SET_NUM_BOARDS_VISIBLE,
);

export const resetNumBoardsVisible = () =>
  setNumBoardsVisible(initialState.numBoardsVisible);

export const increaseNumBoardsVisible = (
  sectionName: keyof NumBoardsVisible,
  increaseBy: number,
): StandardThunkAction<void> => {
  return (dispatch, getState) => {
    const { numBoardsVisible } = getState().ui.superHome;
    dispatch(
      setNumBoardsVisible({
        ...numBoardsVisible,
        [sectionName]: numBoardsVisible[sectionName] + increaseBy,
      }),
    );
  };
};

interface SubmitReplyPayload {
  idReply: string;
  idCard: string;
  text: string;
}

export const submitReply = ({
  idReply,
  idCard,
  text,
}: SubmitReplyPayload): StandardThunkAction => {
  return async (dispatch, getState) => {
    try {
      dispatch(submitReplyRequest());

      await dispatch(addCardComment({ idCard, text }));

      dispatch(submitReplySuccess());
      dispatch(closeReply({ id: idReply }));
    } catch (err) {
      dispatch(submitReplyError(err));
    }
  };
};



// WEBPACK FOOTER //
// ./app/gamma/src/modules/state/ui/super-home.ts